import { z } from 'zod';
import { SPECIALTIES, TASK_TYPES, SENIORITY_LEVELS, LANGUAGES } from '../constants/index.js';
import { cpfSchema, cnpjSchema, professionalAddressSchema } from './auth.js';

/**
 * A single certification the lawyer claims (LL.M., specialization, OAB Federal
 * member, etc). `verified` is only writable by admins — when a lawyer PATCHes
 * their own profile we ignore that field. Admins flip it via a dedicated
 * endpoint after reviewing the comprovante.
 */
export const lawyerCertificationSchema = z.object({
  id: z.string().min(1, 'ID obrigatório'),
  title: z.string().min(3, 'Título muito curto').max(200),
  issuer: z.string().min(2, 'Instituição emissora').max(200),
  issuedYear: z
    .number()
    .int()
    .min(1950, 'Ano inválido')
    .max(new Date().getFullYear() + 1, 'Ano no futuro')
    .optional(),
  // External link to the certificate scan/proof. Future: replace with S3 upload.
  fileUrl: z.string().url('URL inválida').optional(),
  verified: z.boolean().default(false),
  verifiedAt: z.string().datetime().optional(),
  verifiedBy: z.string().optional(),
});
export type LawyerCertification = z.infer<typeof lawyerCertificationSchema>;

export const updateLawyerProfileSchema = z.object({
  bio: z.string().min(200, 'Bio mínima de 200 caracteres').max(2000).optional(),
  linkedinUrl: z.string().url().optional(),
  specialties: z
    .array(z.enum(SPECIALTIES))
    .min(1, 'Escolha ao menos uma especialidade')
    .max(15, 'Máximo de 15 especialidades')
    .optional(),
  // Free-form tags capturam nichos emergentes ou hiper-específicos que não
  // entram no enum: SAFE, NDA bilateral, vesting, Lei do Bem, Marco Civil…
  // Tamanho/qtd limitados pra evitar SEO abuse ou poluição do card.
  tags: z
    .array(z.string().min(2, 'Tag muito curta').max(40, 'Tag muito longa'))
    .max(20, 'Máximo de 20 tags')
    .optional(),
  avatarUrl: z.string().url().optional(),
  seniority: z.enum(SENIORITY_LEVELS).optional(),
  certifications: z.array(lawyerCertificationSchema).max(20, 'Máximo 20 certificações').optional(),
  // Campos do cadastro expandido — todos opcionais aqui porque o lawyer
  // pode editá-los gradativamente. O signup já valida o conjunto mínimo.
  cpf: cpfSchema.optional(),
  cnpj: cnpjSchema.optional(),
  oab: z.string().regex(/^\d{1,8}$/, 'Número da OAB inválido').optional(),
  oabState: z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'UF em maiúsculo')
    .optional(),
  oabCardFileUrl: z.string().url('URL inválida').optional(),
  oabRegularityCertFileUrl: z.string().url('URL inválida').optional(),
  professionalPhotoUrl: z.string().url('URL inválida').optional(),
  // Public booking link (Calendly, Cal.com, etc) — surfaced on call/meeting
  // cards so clientes pick a slot directly instead of "envio um email pra
  // marcar". Native calendar integration (Google Cal / Daily.co) lands later.
  bookingUrl: z.string().url('URL inválida').optional(),
  // Marketplace filters — used in /advogados to refine search. Optional during
  // signup; lawyer fills out in /lawyer/perfil.
  languages: z.array(z.enum(LANGUAGES)).max(LANGUAGES.length).optional(),
  servesIndividuals: z.boolean().optional(),
  servesCompanies: z.boolean().optional(),
  availableHoursPerWeek: z.number().int().min(0).max(80).optional(),
  address: professionalAddressSchema.optional(),
  // Aceites: uma vez true, ficam true. Não permitimos voltar pra false via
  // PATCH (o backend ignora `false`).
  acceptedEthicsCode: z.literal(true).optional(),
  acceptedPlatformTerms: z.literal(true).optional(),
  declaredNoImpediment: z.literal(true).optional(),
  acceptedAt: z.string().datetime().optional(),
});

const taskTypeEnum = z.enum(Object.values(TASK_TYPES) as [string, ...string[]]);

export const lawyerPricingItemSchema = z
  .object({
    taskType: taskTypeEnum,
    // Allowed to be 0 only when active=false — keeps deactivated rows around so the
    // lawyer's previous price isn't silently lost when toggling a service off.
    basePrice: z.number().int().nonnegative(),
    description: z.string().max(500).optional(),
    // Free-form qualifier inside a taskType — lets a lawyer offer
    // "contract_review_simple — Locação residencial" and
    // "contract_review_simple — Honorários trabalhistas" with different prices.
    // Empty/undefined means the row covers any sub-context for that taskType.
    subType: z.string().max(120).optional(),
    // Delivery sizing — exactly one of these (or none) is meaningful for a row.
    // The frontend picks which to show based on taskType (chat→days,
    // call/meeting→minutes, everything else→hours).
    estimatedDeliveryHours: z.number().int().positive().max(720).optional(),
    estimatedDurationMinutes: z.number().int().positive().max(480).optional(),
    accessDurationDays: z.number().int().positive().max(365).optional(),
    // Chat-specific commitments — visible on the public profile so the cliente
    // knows what to expect before paying.
    slaResponseHours: z.number().int().positive().max(168).optional(),
    messageLimit: z.number().int().positive().max(10000).optional(),
    active: z.boolean().default(true),
  })
  .refine((v) => !v.active || v.basePrice > 0, {
    message: 'Preço deve ser maior que zero quando o serviço está ativo',
    path: ['basePrice'],
  });

export const updateLawyerPricingSchema = z.object({
  // Up to 25 because BatchWrite is bounded at 25 items per call. With subType
  // we expect lawyers to have 6-15 rows in practice.
  pricing: z.array(lawyerPricingItemSchema).min(1, 'Defina ao menos um preço').max(25, 'Máximo 25 itens por tabela'),
});

export const lawyerListQuerySchema = z.object({
  specialty: z.enum(SPECIALTIES).optional(),
  language: z.enum(LANGUAGES).optional(),
  servesIndividuals: z.coerce.boolean().optional(),
  servesCompanies: z.coerce.boolean().optional(),
  oabVerified: z.coerce.boolean().optional(),
  minPrice: z.coerce.number().int().nonnegative().optional(),
  maxPrice: z.coerce.number().int().nonnegative().optional(),
  minRating: z.coerce.number().min(0).max(5).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type UpdateLawyerProfileInput = z.infer<typeof updateLawyerProfileSchema>;
export type UpdateLawyerPricingInput = z.infer<typeof updateLawyerPricingSchema>;
export type LawyerListQuery = z.infer<typeof lawyerListQuerySchema>;
